import { useState, useEffect, useRef } from 'react'
import { TextInput, Popover, Box, ActionIcon, Text, Group } from '@mantine/core'
import { IconCalendar, IconChevronLeft, IconChevronRight, IconX } from '@tabler/icons-react'

interface SmartDateInputProps {
  label?: React.ReactNode
  placeholder?: string
  value?: string
  onChange?: (val: string) => void
  clearable?: boolean
  error?: string
  disabled?: boolean
  description?: string
  min?: string
  max?: string
  size?: string
}

const MONTHS_EN = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const DAYS = ['Su','Mo','Tu','We','Th','Fr','Sa']

function isoToDisplay(iso?: string): string {
  if (!iso) return ''
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso
}

function parseToISO(input: string): string {
  const s = input.trim()
  if (!s) return ''
  const dmy = s.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/)
  if (dmy) {
    const [, d, mo, y] = dmy
    const date = new Date(`${y}-${mo.padStart(2,'0')}-${d.padStart(2,'0')}T00:00:00`)
    if (!isNaN(date.getTime())) return `${y}-${mo.padStart(2,'0')}-${d.padStart(2,'0')}`
  }
  const ymd = s.match(/^(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})$/)
  if (ymd) {
    const [, y, mo, d] = ymd
    const date = new Date(`${y}-${mo.padStart(2,'0')}-${d.padStart(2,'0')}T00:00:00`)
    if (!isNaN(date.getTime())) return `${y}-${mo.padStart(2,'0')}-${d.padStart(2,'0')}`
  }
  return ''
}

function CalendarPicker({ value, onChange, onClose, min, max }: {
  value?: string; onChange: (iso: string) => void; onClose: () => void; min?: string; max?: string
}) {
  const today = new Date()
  const init = value ? new Date(value + 'T00:00:00') : today
  const [viewYear, setViewYear] = useState(init.getFullYear())
  const [viewMonth, setViewMonth] = useState(init.getMonth())
  const [mode, setMode] = useState<'day'|'month'|'year'>('day')
  const startYear = Math.floor(viewYear / 12) * 12

  const firstDay = new Date(viewYear, viewMonth, 1).getDay()
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
  const prevDays = new Date(viewYear, viewMonth, 0).getDate()
  const cells: { day: number; curr: boolean }[] = []
  for (let i = firstDay - 1; i >= 0; i--) cells.push({ day: prevDays - i, curr: false })
  for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, curr: true })
  while (cells.length % 7 !== 0) cells.push({ day: cells.length - firstDay - daysInMonth + 1, curr: false })

  const isSelected = (day: number) =>
    !!value && value === `${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`
  const isToday = (day: number) =>
    today.getFullYear() === viewYear && today.getMonth() === viewMonth && today.getDate() === day
  // ── ช่วงวันที่ที่เลือกได้ (ถ้ามี min/max) — เซลล์นอกช่วงกดไม่ได้ กันเลือกวันที่ผิดเงื่อนไข
  // ธุรกิจ (เช่น "ขอขยายเวลาได้ไม่เกิน N วัน") ตั้งแต่ตอนเลือกในปฏิทินเลย ไม่ใช่แค่ตอน validate
  // ทีหลัง ──
  const isDisabled = (day: number) => {
    const iso = `${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`
    if (min && iso < min) return true
    if (max && iso > max) return true
    return false
  }

  const prevNav = () => {
    if (mode === 'day') { let m = viewMonth-1, y = viewYear; if (m<0){m=11;y--} setViewMonth(m); setViewYear(y) }
    else if (mode === 'year') setViewYear(startYear - 12)
  }
  const nextNav = () => {
    if (mode === 'day') { let m = viewMonth+1, y = viewYear; if (m>11){m=0;y++} setViewMonth(m); setViewYear(y) }
    else if (mode === 'year') setViewYear(startYear + 12)
  }

  return (
    <Box style={{ width: 256, fontFamily: 'Inter, sans-serif', fontSize: 12 }}>
      <Group justify="space-between" px={8} py={6} style={{ borderBottom: '1px solid #f1f5f9' }}>
        <ActionIcon size="sm" variant="subtle" onClick={prevNav}><IconChevronLeft size={13}/></ActionIcon>
        <Group gap={4}>
          {mode === 'day' && <>
            <Text size="xs" fw={600} style={{ cursor:'pointer' }} onClick={() => setMode('month')}>{MONTHS_EN[viewMonth]}</Text>
            <Text size="xs" fw={600} style={{ cursor:'pointer' }} onClick={() => setMode('year')}>{viewYear}</Text>
          </>}
          {mode === 'month' && <Text size="xs" fw={600} style={{ cursor:'pointer' }} onClick={() => setMode('year')}>{viewYear}</Text>}
          {mode === 'year' && <Text size="xs" fw={600}>{startYear}–{startYear+11}</Text>}
        </Group>
        <ActionIcon size="sm" variant="subtle" onClick={nextNav}><IconChevronRight size={13}/></ActionIcon>
      </Group>

      {mode === 'day' && <Box p={6}>
        <Group gap={0} mb={4}>
          {DAYS.map(d => <Box key={d} style={{ width:'14.28%', textAlign:'center' }}><Text size="10px" c="dimmed" fw={600}>{d}</Text></Box>)}
        </Group>
        <Box style={{ display:'flex', flexWrap:'wrap' }}>
          {cells.map((c,i) => (
            <Box key={i} style={{ width:'14.28%', padding:'1px 0', textAlign:'center' }}>
              <Box onClick={() => { if(c.curr && !isDisabled(c.day)){ const iso=`${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(c.day).padStart(2,'0')}`; onChange(iso); onClose() }}}
                style={{
                  margin:'0 auto', width:28, height:28, borderRadius:'50%', display:'flex',
                  alignItems:'center', justifyContent:'center', fontSize:11,
                  cursor: c.curr && !isDisabled(c.day) ? 'pointer' : 'default',
                  background: isSelected(c.day)&&c.curr ? '#0f172a' : 'transparent',
                  color: isSelected(c.day)&&c.curr ? '#fff' : !c.curr || (c.curr && isDisabled(c.day)) ? '#cbd5e1' : isToday(c.day) ? '#0d9488' : '#0f172a',
                  border: isToday(c.day)&&!isSelected(c.day) ? '1.5px solid #0d9488' : 'none',
                  fontWeight: isSelected(c.day)&&c.curr ? 700 : 400,
                }}
                onMouseEnter={e => { if(c.curr&&!isSelected(c.day)&&!isDisabled(c.day)) (e.currentTarget as HTMLElement).style.background='#f1f5f9' }}
                onMouseLeave={e => { if(c.curr&&!isSelected(c.day)&&!isDisabled(c.day)) (e.currentTarget as HTMLElement).style.background='transparent' }}
              >{c.day}</Box>
            </Box>
          ))}
        </Box>
        <Box style={{ textAlign:'center', marginTop:4, paddingTop:4, borderTop:'1px solid #f1f5f9' }}>
          <Text size="xs" c="teal" fw={500} style={{ cursor:'pointer' }}
            onClick={() => { setViewYear(today.getFullYear()); setViewMonth(today.getMonth()); const iso=`${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`; onChange(iso); onClose() }}>
            Today
          </Text>
        </Box>
      </Box>}

      {mode === 'month' && <Box p={8} style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
        {MONTHS_EN.map((m,i) => (
          <Box key={m} onClick={() => { setViewMonth(i); setMode('day') }}
            style={{ width:'calc(33.33% - 3px)', textAlign:'center', padding:'6px 0', borderRadius:6, cursor:'pointer', fontSize:11, fontWeight: viewMonth===i?700:400, background: viewMonth===i?'#0f172a':'transparent', color: viewMonth===i?'#fff':'#0f172a' }}>
            {m}
          </Box>
        ))}
      </Box>}

      {mode === 'year' && <Box p={8} style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
        {Array.from({length:12},(_,i)=>startYear+i).map(yr => (
          <Box key={yr} onClick={() => { setViewYear(yr); setMode('month') }}
            style={{ width:'calc(33.33% - 3px)', textAlign:'center', padding:'6px 0', borderRadius:6, cursor:'pointer', fontSize:11, fontWeight: viewYear===yr?700:400, background: viewYear===yr?'#0f172a':'transparent', color: viewYear===yr?'#fff':'#0f172a' }}>
            {yr}
          </Box>
        ))}
      </Box>}
    </Box>
  )
}

export default function SmartDateInput({ label, placeholder='DD/MM/YYYY', value, onChange, clearable, error, disabled, description, min, max, size }: SmartDateInputProps) {
  const [raw, setRaw] = useState('')
  const [opened, setOpened] = useState(false)

  useEffect(() => { setRaw(isoToDisplay(value)) }, [value])

  // ── พิมพ์อิสระได้เสมอ แต่ถ้ามี min/max กำกับไว้ (เช่น "ขอขยายได้ไม่เกิน N วัน") วันที่ที่พิมพ์มา
  // นอกช่วงจะถูกดึงกลับเข้าขอบเขตแทนที่จะปล่อยผ่านเงียบๆ — ยังพิมพ์ได้อิสระ แค่ผลลัพธ์สุดท้ายต้อง
  // อยู่ในเงื่อนไขธุรกิจเดิมเสมอ เหมือนตอนใช้ native date input ที่มี min/max กำกับ ──
  const clamp = (iso: string): string => {
    if (min && iso < min) return min
    if (max && iso > max) return max
    return iso
  }

  const handleBlur = () => {
    const s = raw.trim()
    if (!s) { onChange?.(''); return }
    const iso = parseToISO(s)
    if (iso) { const clamped = clamp(iso); const [y,mo,d]=clamped.split('-'); setRaw(`${d}/${mo}/${y}`); onChange?.(clamped) }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').trim()
    const iso = parseToISO(pasted)
    if (iso) { e.preventDefault(); const clamped = clamp(iso); const [y,mo,d]=clamped.split('-'); setRaw(`${d}/${mo}/${y}`); onChange?.(clamped) }
  }

  return (
    <Popover opened={opened} onClose={() => setOpened(false)} position="bottom-start" withinPortal shadow="md" radius="md" offset={4}>
      <Popover.Target>
        <TextInput
          label={label} placeholder={placeholder} value={raw} error={error} disabled={disabled} description={description} size={size}
          onChange={e => setRaw(e.target.value)}
          onBlur={handleBlur}
          onPaste={handlePaste}
          rightSection={
            <Group gap={2} wrap="nowrap">
              {clearable && raw && (
                <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => { setRaw(''); onChange?.('') }}>
                  <IconX size={11}/>
                </ActionIcon>
              )}
              <ActionIcon size="sm" variant="subtle" color="gray" disabled={disabled} onClick={() => setOpened(o=>!o)}>
                <IconCalendar size={14}/>
              </ActionIcon>
            </Group>
          }
          rightSectionWidth={clearable && raw ? 52 : 32}
        />
      </Popover.Target>
      <Popover.Dropdown p={0} style={{ border:'1px solid #e2e8f0' }}>
        <CalendarPicker value={value} min={min} max={max} onChange={iso => { const clamped = clamp(iso); const [y,mo,d]=clamped.split('-'); setRaw(`${d}/${mo}/${y}`); onChange?.(clamped); setOpened(false) }} onClose={() => setOpened(false)} />
      </Popover.Dropdown>
    </Popover>
  )
}
